import React, { useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  alpha,
  useTheme,
  Stack,
  Grow,
  Fade,
  keyframes,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowForward as ArrowForwardIcon,
  School as SchoolIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useGetOnboardingStatusQuery } from "components/Slices/usersApiSlice";

// CSS-based confetti animation
const confettiFall = keyframes`
  0% {
    transform: translateY(-100vh) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
`;

const confettiColors = [
  "#2E7D32",
  "#4CAF50",
  "#81C784",
  "#FFD700",
  "#FFA500",
  "#1976D2",
];

const ConfettiPiece = ({
  delay,
  left,
  color,
}: {
  delay: number;
  left: string;
  color: string;
}) => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left,
      width: "10px",
      height: "10px",
      backgroundColor: color,
      borderRadius: Math.random() > 0.5 ? "50%" : "2px",
      animation: `${confettiFall} 3s ease-in-out ${delay}s forwards`,
      zIndex: 1000,
    }}
  />
);

export default function OnboardingSuccess() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Check if user skipped the bank setup step
  const skippedBankSetup = useMemo(() => {
    return sessionStorage.getItem("skippedBankSetup") === "true";
  }, []);

  // This page is reachable with verification SKIPPED (the verification step has
  // a "skip for now" path), so never claim "identity verified" from the mere
  // fact of landing here — ask the backend what actually happened.
  const { data: onboardingData } = useGetOnboardingStatusQuery();
  const status = onboardingData?.status?.toLowerCase();
  const isVerified = status === "active" || status === "complete";
  const isVerifying = status === "verifying";

  const benefits = [
    {
      icon: <GroupsIcon sx={{ fontSize: 32 }} />,
      title: "Join Circles",
      description: "Connect with friends and family in savings circles",
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: 32 }} />,
      title: "Make Payments",
      description: "Contribute to your circles securely and easily",
    },
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 32 }} />,
      title: "Receive Payouts",
      description: "Get your turn's payout directly to your bank",
    },
  ];

  // Generate confetti pieces
  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 30 }, () => ({
        delay: Math.random() * 2,
        left: `${Math.random() * 100}%`,
        color:
          confettiColors[Math.floor(Math.random() * confettiColors.length)],
      })),
    [],
  );

  return (
    <Box
      sx={{
        // minHeight, not maxHeight: with maxHeight + overflow:hidden the page
        // CLIPPED on phones — centered content lost its top (success icon) and
        // bottom (the "Go to My Circles" CTA) with no way to scroll. overflow
        // hidden stays: it contains the falling confetti.
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.success.main,
          0.05,
        )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* CSS Confetti */}
      {confettiPieces.map((piece, i) => (
        <ConfettiPiece key={i} {...piece} />
      ))}

      <Container maxWidth="sm">
        <Grow in timeout={500}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              p: { xs: 4, sm: 5 },
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Success Icon */}
            <Fade in timeout={800}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": {
                      boxShadow: `0 0 0 0 ${alpha(
                        theme.palette.success.main,
                        0.4,
                      )}`,
                    },
                    "70%": {
                      boxShadow: `0 0 0 20px ${alpha(
                        theme.palette.success.main,
                        0,
                      )}`,
                    },
                    "100%": {
                      boxShadow: `0 0 0 0 ${alpha(
                        theme.palette.success.main,
                        0,
                      )}`,
                    },
                  },
                }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 56,
                    color: theme.palette.success.main,
                  }}
                />
              </Box>
            </Fade>

            {/* Title — conditional based on bank setup */}
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="text.primary"
                  gutterBottom
                >
                  {isVerified
                    ? skippedBankSetup
                      ? "You're Almost Ready!"
                      : "You're All Set!"
                    : isVerifying
                      ? "Verification In Progress"
                      : "Welcome to Sanduuq!"}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: skippedBankSetup || !isVerified ? 2 : 4,
                    maxWidth: 400,
                    mx: "auto",
                  }}
                >
                  {isVerified
                    ? skippedBankSetup
                      ? "Your identity is verified and your account is ready. Just one more thing before you can receive payouts."
                      : "Your identity has been verified and your bank account is linked. Welcome to Sanduuq!"
                    : isVerifying
                      ? "We're reviewing your identity — this usually takes a few minutes. You can start exploring circles right away."
                      : "Your account is ready — you can join or create circles now. Verify your identity before your first payout."}
                </Typography>
              </Box>
            </Fade>

            {/* Verification reminder — user skipped identity verification */}
            {!isVerified && !isVerifying && (
              <Fade in timeout={1050}>
                <Alert
                  severity="info"
                  sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}
                  action={
                    <Button
                      color="info"
                      size="small"
                      onClick={() => navigate("/onboarding")}
                      sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      Verify Now
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={500}>
                    Identity verification is still pending
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Finish it anytime — we'll also remind you before your first
                    payout.
                  </Typography>
                </Alert>
              </Fade>
            )}

            {/* Bank setup reminder — shown only if skipped (and verified) */}
            {isVerified && skippedBankSetup && (
              <Fade in timeout={1050}>
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    textAlign: "left",
                  }}
                  action={
                    <Button
                      color="warning"
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={() => navigate("/settings?tab=payout")}
                      sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      Add Now
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={500}>
                    Add a bank account to receive payouts
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You can do this anytime in Settings before your first
                    payout.
                  </Typography>
                </Alert>
              </Fade>
            )}

            {/* Learn How It Works - First Block */}
            <Fade in timeout={1100}>
              <Box
                component={RouterLink}
                to="/how-it-works"
                target="_blank"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  textAlign: "left",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    borderColor: theme.palette.info.main,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.info.main,
                    flexShrink: 0,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Learn How Circles Work
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New to lending circles? Learn how they work
                  </Typography>
                </Box>
                <OpenInNewIcon
                  sx={{ color: theme.palette.info.main, fontSize: 20 }}
                />
              </Box>
            </Fade>

            {/* Benefits */}
            <Fade in timeout={1200}>
              <Stack spacing={2} sx={{ mb: 4 }}>
                {benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1,
                      )}`,
                      textAlign: "left",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    >
                      {benefit.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Fade>

            {/* CTA Button */}
            <Fade in timeout={1400}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/view-group")}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.4,
                  )}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.5,
                    )}`,
                  },
                }}
              >
                View My Circles
              </Button>
            </Fade>
          </Paper>
        </Grow>

        {/* Additional Info */}
        <Fade in timeout={1600}>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Need help getting started?{" "}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate("/create-group")}
                sx={{ fontWeight: 600 }}
              >
                Create your first Circle
              </Button>
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}
